/** Locale chosen so numeric dates are unambiguous: day + short English month + year (e.g. 11 May 2026). */
const DISPLAY_LOCALE = 'en-GB';

function parseToDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  return d;
}

/**
 * Format a calendar day from an ISO-like string using YYYY-MM-DD components in the local timezone.
 * Use for planning week boundaries and other values where the API stores a calendar date and the UI
 * previously showed `slice(0, 10)` without timezone shifting the displayed day.
 */
export function formatDisplayCalendarDay(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return '—';
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const day = Number(m[3]);
  const local = new Date(y, mo, day);
  if (isNaN(local.getTime())) return '—';
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(local);
}

/** Date-only: D MMM YYYY (e.g. 4 Feb 2026). */
export function formatDisplayDate(value: string | Date | null | undefined): string {
  const d = parseToDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

/** Time only (24-hour, minutes). For bubble timestamps where the date is shown separately. */
export function formatDisplayTime(value: string | Date | null | undefined): string {
  const d = parseToDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/** Date and time: D MMM YYYY, HH:mm (24-hour). */
export function formatDisplayDateTime(value: string | Date | null | undefined): string {
  const d = parseToDate(value);
  if (!d) return '—';
  const datePart = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
  const timePart = formatDisplayTime(d);
  return `${datePart}, ${timePart}`;
}

/** Prefer calendar-day parsing only for a bare `YYYY-MM-DD` string; otherwise same as {@link formatDisplayDate}. */
export function formatShortDisplayDate(value: string | Date | null | undefined): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return formatDisplayCalendarDay(value);
  }
  return formatDisplayDate(value);
}

/**
 * Inclusive range for planning-style strings or Date values.
 * Uses calendar-day parsing for string inputs so week labels match stored YYYY-MM-DD.
 */
export function formatDisplayDateRange(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
): string {
  const a =
    typeof start === 'string' ? formatDisplayCalendarDay(start) : formatDisplayDate(start);
  const b = typeof end === 'string' ? formatDisplayCalendarDay(end) : formatDisplayDate(end);
  if (a === '—' || b === '—') return '—';
  return `${a} – ${b}`;
}
